/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { db } from '../../../firebase'
import {
  Box,
  Button,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  Modal,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider
} from "@mui/material"
import dayjs from "dayjs"
import { toast } from 'react-toastify'
import { setIndividualStatus } from "./common"

type Props = {
  individualId: string
  isOpen: boolean
  onClose: (open: boolean) => void
}

interface CheckItem {
  value: string
  issue?: string
  action?: string
}

interface ProcessingRecord {
  // 基本情報
  date: string
  worker: string
  individualNumber: string

  // 分割・脱骨・細切
  knifeWashing: CheckItem
  handWashing: CheckItem
  contactPrevention: CheckItem
  bulletRemoval: CheckItem

  // 包装・印字・保管・出荷
  storageTemp: CheckItem
  packagingCheck: CheckItem
  cardboardHandling: CheckItem

  // 処理作業全般
  secondaryContamination: CheckItem
  toolSeparation: CheckItem

  // 不良内容
  issues: string

  // 確認者
  recorder: string
  verifier: string

  // タイムスタンプ
  createdAt?: string
  updatedAt?: string
}

const ProcessingRecordModal = (props: Props) => {
  const { individualId, isOpen, onClose } = props
  const [open, setOpen] = useState<boolean>(false)
  const [processing, setProcessing] = useState<boolean>(false)
  const [individualInfo, setIndividualInfo] = useState<any>()

  // フォーム状態
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const [worker, setWorker] = useState<string>('')

  // チェック項目（分割・脱骨・細切）
  const [knifeWashing, setKnifeWashing] = useState<string>('○')
  const [handWashing, setHandWashing] = useState<string>('○')
  const [contactPrevention, setContactPrevention] = useState<string>('○')
  const [bulletRemoval, setBulletRemoval] = useState<string>('○')

  // チェック項目（包装・印字・保管・出荷）
  const [storageTemp, setStorageTemp] = useState<string>('○')
  const [packagingCheck, setPackagingCheck] = useState<string>('○')
  const [cardboardHandling, setCardboardHandling] = useState<string>('○')

  // チェック項目（処理作業全般）
  const [secondaryContamination, setSecondaryContamination] = useState<string>('○')
  const [toolSeparation, setToolSeparation] = useState<string>('○')

  // 不良内容と措置
  const [issues, setIssues] = useState<string>('')

  // 確認者
  const [recorder, setRecorder] = useState<string>('')
  const [verifier, setVerifier] = useState<string>('')

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    if (isOpen && individualId) {
      loadIndividualInfo()
      loadExistingRecord()
    }
  }, [isOpen, individualId])

  const loadIndividualInfo = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'individuals', individualId))
      if (docSnap.exists()) {
        setIndividualInfo(docSnap.data())
      }
    } catch (e) {
      console.error("個体情報の取得に失敗:", e)
    }
  }

  const loadExistingRecord = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'processingRecords', individualId))
      if (docSnap.exists()) {
        const data = docSnap.data()
        // 既存データがある場合は復元
        setDate(data.date || dayjs().format('YYYY-MM-DD'))
        setWorker(data.worker || '')
        setKnifeWashing(data.knifeWashing?.value || '○')
        setHandWashing(data.handWashing?.value || '○')
        setContactPrevention(data.contactPrevention?.value || '○')
        setBulletRemoval(data.bulletRemoval?.value || '○')
        setStorageTemp(data.storageTemp?.value || '○')
        setPackagingCheck(data.packagingCheck?.value || '○')
        setCardboardHandling(data.cardboardHandling?.value || '○')
        setSecondaryContamination(data.secondaryContamination?.value || '○')
        setToolSeparation(data.toolSeparation?.value || '○')
        setIssues(data.issues || '')
        setRecorder(data.recorder || '')
        setVerifier(data.verifier || '')
      }
    } catch (e) {
      console.error("既存レコードの取得に失敗:", e)
    }
  }

  const handleSave = async () => {
    // バリデーション
    if (!worker) {
      toast.error("作業者を入力してください")
      return
    }
    if (!recorder) {
      toast.error("記録者を入力してください")
      return
    }
    if (!verifier) {
      toast.error("確認者を入力してください")
      return
    }

    // ×がある場合は措置内容が必須
    const hasIssue = [
      knifeWashing, handWashing, contactPrevention, bulletRemoval,
      storageTemp, packagingCheck, cardboardHandling,
      secondaryContamination, toolSeparation
    ].some(item => item === '×')

    if (hasIssue && !issues.trim()) {
      toast.error("不良があった場合は内容と措置を記入してください")
      return
    }

    setProcessing(true)
    try {
      const recordData: ProcessingRecord = {
        date,
        worker,
        individualNumber: individualInfo?.individualNo || '',
        knifeWashing: { value: knifeWashing },
        handWashing: { value: handWashing },
        contactPrevention: { value: contactPrevention },
        bulletRemoval: { value: bulletRemoval },
        storageTemp: { value: storageTemp },
        packagingCheck: { value: packagingCheck },
        cardboardHandling: { value: cardboardHandling },
        secondaryContamination: { value: secondaryContamination },
        toolSeparation: { value: toolSeparation },
        issues,
        recorder,
        verifier,
        updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss')
      }

      // 既存レコードがない場合は createdAt を追加
      const existingDoc = await getDoc(doc(db, 'processingRecords', individualId))
      if (!existingDoc.exists()) {
        recordData.createdAt = dayjs().format('YYYY-MM-DD HH:mm:ss')
      }

      // 加工記録を保存
      await setDoc(doc(db, 'processingRecords', individualId), recordData)

      // 個体情報を「加工中」ステータスに更新（processingStartDateを設定）
      await updateDoc(doc(db, 'individuals', individualId),
        setIndividualStatus({
          ...individualInfo,
          processingStartDate: date,
          updatedAt: dayjs().format('YYYY/MM/DD HH:mm')
        })
      )

      toast.success("加工記録を保存しました")
      handleClose()
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleComplete = async () => {
    // まず保存処理を実行
    if (!worker || !recorder || !verifier) {
      toast.error("必須項目を入力してください")
      return
    }

    const hasIssue = [
      knifeWashing, handWashing, contactPrevention, bulletRemoval,
      storageTemp, packagingCheck, cardboardHandling,
      secondaryContamination, toolSeparation
    ].some(item => item === '×')

    if (hasIssue && !issues.trim()) {
      toast.error("不良があった場合は内容と措置を記入してください")
      return
    }

    setProcessing(true)
    try {
      const recordData: ProcessingRecord = {
        date,
        worker,
        individualNumber: individualInfo?.individualNo || '',
        knifeWashing: { value: knifeWashing },
        handWashing: { value: handWashing },
        contactPrevention: { value: contactPrevention },
        bulletRemoval: { value: bulletRemoval },
        storageTemp: { value: storageTemp },
        packagingCheck: { value: packagingCheck },
        cardboardHandling: { value: cardboardHandling },
        secondaryContamination: { value: secondaryContamination },
        toolSeparation: { value: toolSeparation },
        issues,
        recorder,
        verifier,
        updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss')
      }

      const existingDoc = await getDoc(doc(db, 'processingRecords', individualId))
      if (!existingDoc.exists()) {
        recordData.createdAt = dayjs().format('YYYY-MM-DD HH:mm:ss')
      }

      // 加工記録を保存
      await setDoc(doc(db, 'processingRecords', individualId), recordData)

      // 個体情報を「加工済」ステータスに更新（processingDateを設定）
      await updateDoc(doc(db, 'individuals', individualId),
        setIndividualStatus({
          ...individualInfo,
          processingStartDate: individualInfo.processingStartDate || date,
          processingDate: dayjs().format('YYYY/MM/DD'),
          updatedAt: dayjs().format('YYYY/MM/DD HH:mm')
        })
      )

      toast.success("加工記録を保存し、加工済として完了しました")
      handleClose()
    } catch (e) {
      toast.error("完了処理に失敗しました: " + JSON.stringify(e))
    } finally {
      setProcessing(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
    onClose(false)
  }

  const renderCheckItem = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    itemNumber: string
  ) => (
    <Box mb={2}>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        {itemNumber}. {label}
      </Typography>
      <RadioGroup
        row
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <FormControlLabel value="○" control={<Radio />} label="○：良好" />
        <FormControlLabel value="△" control={<Radio />} label="△：おおむね良好" />
        <FormControlLabel value="×" control={<Radio />} label="×：不良" />
      </RadioGroup>
    </Box>
  )

  return (
    <Modal
      open={open}
      aria-labelledby="processing-record-modal"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClose={handleClose}
    >
      <Box
        bgcolor="white"
        sx={{
          width: '90vw',
          maxWidth: '1000px',
          maxHeight: '90vh',
          overflow: 'auto',
          p: 3
        }}
      >
        <Typography variant="h5" fontWeight="bold" mb={2}>
          食肉処理作業の点検②（分割・脱骨・細切〜梱包・出荷）
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={3}>
          個体識別番号：{individualInfo?.individualNo || ''}
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
          {/* 基本情報 */}
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
            <TextField
              label="記録日"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="作業者"
              value={worker}
              onChange={(e) => setWorker(e.target.value)}
              fullWidth
              required
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 分割・脱骨・細切 */}
          <Typography variant="h6" fontWeight="bold" mb={2}>
            分割・脱骨・細切
          </Typography>
          {renderCheckItem(
            'ナイフは１作業ごとに洗浄・消毒されているか',
            knifeWashing,
            setKnifeWashing,
            '1'
          )}
          {renderCheckItem(
            '手指、前掛けは１作業ごとに洗浄されているか',
            handWashing,
            setHandWashing,
            '2'
          )}
          {renderCheckItem(
            '枝肉が冷蔵／冷凍庫の入口や壁、その他の設備等に接触していないか',
            contactPrevention,
            setContactPrevention,
            '3'
          )}
          {renderCheckItem(
            '弾痕周辺を確認し、弾丸を確実に除去し、分割したか（弾丸の残存はないか）',
            bulletRemoval,
            setBulletRemoval,
            '4'
          )}

          <Divider sx={{ my: 3 }} />

          {/* 包装・印字・保管・出荷 */}
          <Typography variant="h6" fontWeight="bold" mb={2}>
            包装・印字・保管・出荷
          </Typography>
          {renderCheckItem(
            '保管温度は適切か',
            storageTemp,
            setStorageTemp,
            '5'
          )}
          {renderCheckItem(
            '包装用ビニール、梱包用段ボールの破損、汚染はないか',
            packagingCheck,
            setPackagingCheck,
            '6'
          )}
          {renderCheckItem(
            '段ボールを衛生区域に持ち込まなかったか、又は消毒後持ち込んだか',
            cardboardHandling,
            setCardboardHandling,
            '7'
          )}

          <Divider sx={{ my: 3 }} />

          {/* 処理作業全般 */}
          <Typography variant="h6" fontWeight="bold" mb={2}>
            処理作業全般
          </Typography>
          {renderCheckItem(
            '結露、床面等からのはね水による二次汚染を防止したか',
            secondaryContamination,
            setSecondaryContamination,
            '8'
          )}
          {renderCheckItem(
            '使用器具は使用区分により使い分けたか',
            toolSeparation,
            setToolSeparation,
            '9'
          )}

          <Divider sx={{ my: 3 }} />

          {/* 不良があった場合の内容と措置 */}
          <Typography variant="h6" fontWeight="bold" mb={2}>
            不良があった場合の内容と措置
          </Typography>
          <TextField
            multiline
            rows={4}
            value={issues}
            onChange={(e) => setIssues(e.target.value)}
            fullWidth
            placeholder="不良があった場合は、区分と点検項目番号、内容、措置を記入してください"
          />

          <Divider sx={{ my: 3 }} />

          {/* 確認者 */}
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
            <TextField
              label="記録者"
              value={recorder}
              onChange={(e) => setRecorder(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="確認者"
              value={verifier}
              onChange={(e) => setVerifier(e.target.value)}
              fullWidth
              required
            />
          </Box>
        </Paper>

        {/* アクションボタン */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={processing}
          >
            閉じる
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={processing}
          >
            保存（加工中）
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleComplete}
            disabled={processing}
          >
            保存して完了（加工済）
          </Button>
        </Box>

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={processing}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Modal>
  )
}

export default ProcessingRecordModal
